import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import { collect } from 'collect.js';
import { useSelector } from 'react-redux';
import { Button, Flex, Segmented, Tag } from 'antd';
import moment from 'moment-timezone';
import FadeIn from '../FadeIn';
import SubscriptionProduct from '../billing/SubscriptionProduct';
import { SubscriptionProductType, SubscriptionTerm, TAX_BEHAVIOUR } from '../../services/exports/Constants';
import Subscription from '../billing/Subscription';
import Agreement from '../billing/Agreement';
import AnnualContractDiscount from '../billing/AnnualContractDiscount';
import {
  ANNUAL_CONTRACT_DISCOUNT,
  clampDiscount,
  discountLineItem,
  maxContractDiscount,
} from '../../services/helpers/ContractDiscountHelpers';
import { nextBillDate } from '../../services/helpers/SubscriptionBillDate';
import PaymentInfoStep from './PaymentInfoStep';
import { Mode, PlanTab } from './SubscriptionStep';
import useApiClient from '../../hooks/api/useApiClient';
import useHelpers from '../../hooks/context/useHelpers';
import TaxCalculator from '../../services/helpers/TaxCalculator';
import {
  useCreateSubscriptionMutation,
  useGetServiceContractQuery,
  useSaveServiceContractMutation,
  useUpdateSubscriptionMutation,
} from '../../store/api-slices';
import usePermissions, { ManagerPermission } from '../../hooks/auth/usePermissions';

const steps = {
  select_products: 0,
  subscribe: 1,
};

/**
 * Subscription step with a service-contract commitment: adds the commitment
 * term (monthly/annual) + annual contract discount on top of the legacy
 * SubscriptionStep flow. Selected for master service providers by
 * SubscriptionStepStrategy.
 */
export default function SubscriptionWithTermStep({
  data,
  subscriptionProducts,
  mode = Mode.preview,
  filterOtherProducts,
  onEdit,
  onSave,
  moveForward,
  providedServiceContract,
  onPersist,
  isPersisting,
  hasTermSelector = true,
  readOnlyProducts,
  renderAgreementText,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Verification:SubscriptionStep' });

  const ref = useRef(null);

  const { CurrentCompanyManager, SubscriptionsManager, ServiceContractsManager } = useApiClient();
  const { can } = usePermissions();
  const { formatCurrency } = useHelpers();
  const [createSubscription, { isLoading: isCreateLoading }] = useCreateSubscriptionMutation();
  const [updateSubscription, { isLoading: isUpdateLoading }] = useUpdateSubscriptionMutation();
  // The commitment term + annual discount live on the service contract. Read it
  // via RTK Query (refetched on mount for fresh data) and persist it through
  // the matching mutation, which invalidates the cache so the query refreshes.
  const { data: _serviceContract } = useGetServiceContractQuery(
    { apiManager: ServiceContractsManager },
    { refetchOnMountOrArgChange: true, skip: !!providedServiceContract },
  );
  const serviceContract = providedServiceContract ?? _serviceContract;
  const [saveContract, { isLoading: isContractSaving }] = useSaveServiceContractMutation();

  const company = useSelector((store) => store.currentCompany?.company);
  const serviceProvider = useSelector((state) => state.currentServiceProvider?.serviceProvider);
  const {
    merchant: { payment_options },
  } = company;
  const hasPaymentOption = payment_options?.length > 0;

  const isLive = company.live_from !== null;
  const hasActiveTrial = isLive && data?.trial_ends_at && moment(data.trial_ends_at).isAfter(moment());

  const [agreementAccepted, setAgreementAccepted] = useState(false);

  const [activePlanTab, setActivePlanTab] = useState(PlanTab.actual);
  // Editable copy of the contract's commitment term + annual discount, seeded
  // from the fetched service contract once it (re)loads.
  const [contract, setContract] = useState({
    term: SubscriptionTerm.Monthly,
    discount: ANNUAL_CONTRACT_DISCOUNT.default,
  });

  useEffect(() => {
    if (serviceContract) {
      setContract({
        term: serviceContract.contract_term ?? SubscriptionTerm.Monthly,
        discount: serviceContract.contract_discount_percentage ?? ANNUAL_CONTRACT_DISCOUNT.default,
      });
    }
  }, [serviceContract]);
  const [step, setStep] = useState(data && mode !== Mode.edit ? steps.subscribe : steps.select_products);
  const [isEdit, setIsEdit] = useState(mode === Mode.edit);
  const [billingMode, setBillingMode] = useState(
    data?.billing_mode ?? serviceProvider.default_subscription_billing_mode,
  );
  const [hasTrial, setHasTrial] = useState(hasActiveTrial || data?.trial_days > 0);
  const [trialDays, setTrialDays] = useState(
    hasActiveTrial && !!data ? moment(data.trial_ends_at).diff(moment(), 'days') + 1 : data?.trial_days,
  );

  const findSubscriptionItem = (product) => collect(data?.items ?? []).firstWhere('product.id', product.id);

  // Products whose type is listed as read-only render without quantity (+/-) or
  // price-edit controls — they are shown but can't be modified in this flow.
  const isReadOnlyProduct = (product) => collect(readOnlyProducts ?? []).contains(product.type);

  const [personalProducts, setPersonalProducts] = useState([]);

  const initialPersonalProducts = useMemo(
    () =>
      collect(subscriptionProducts)
        .map((product) => {
          const subscriptionItem = findSubscriptionItem(product);

          return subscriptionItem ? { ...product, price: subscriptionItem.price } : product;
        })
        .toArray(),
    [data, subscriptionProducts],
  );

  useEffect(() => {
    setPersonalProducts(initialPersonalProducts);
  }, [initialPersonalProducts]);

  const plans = useMemo(
    () => collect(personalProducts).where('type', SubscriptionProductType.Plan).toArray(),
    [personalProducts],
  );
  const legacyPlans = useMemo(
    () => collect(personalProducts).where('type', SubscriptionProductType.LegacyPlan).toArray(),
    [personalProducts],
  );
  const otherProducts = useMemo(
    () =>
      collect(personalProducts)
        .whereNotIn('id', [...collect(plans).pluck('id').toArray(), ...collect(legacyPlans).pluck('id').toArray()])
        .when(!!filterOtherProducts, (collection) => collection.filter(filterOtherProducts))
        .toArray(),
    [personalProducts, plans, legacyPlans, filterOtherProducts],
  );

  const [lineItems, setLineItems] = useState(
    collect(data?.items ?? [])
      .filter((item) => item.product.is_active && item.price.is_active)
      .toArray(),
  );
  // The discount cap: the manager's own cap (15%, or 20% with the elevated
  // permission) widened to any discount already granted on the contract, so a
  // lower-capped manager can keep an elevated discount someone else granted
  // without raising a new one past their own cap (mirrors the backend rule).
  const discountMax = maxContractDiscount(
    can(ManagerPermission.GrantElevatedAnnualContractDiscount),
    serviceContract?.contract_discount_percentage ?? 0,
  );
  // The annual commitment term grants a discount; monthly stays at list price.
  // `lineItems` always holds the original (undiscounted) values — those are
  // what we persist, leaving the backend to recompute the discount itself.
  const effectiveDiscount =
    contract.term === SubscriptionTerm.Annual ? clampDiscount(contract.discount, discountMax) : 0;
  const discountedLineItems = useMemo(
    () =>
      collect(lineItems)
        .map((item) => discountLineItem(item, effectiveDiscount))
        .toArray(),
    [lineItems, effectiveDiscount],
  );

  const amount = useMemo(
    () => collect(discountedLineItems).reduce((carry, item) => carry + item.amount, 0),
    [discountedLineItems],
  );
  const tax = useMemo(
    () =>
      collect(discountedLineItems)
        .where('tax_behaviour', TAX_BEHAVIOUR.exclusive)
        .reduce((carry, item) => carry + item.tax, 0),
    [discountedLineItems],
  );
  const total = useMemo(
    () => collect(discountedLineItems).reduce((carry, item) => carry + item.taxed_amount, 0),
    [discountedLineItems],
  );

  const isPlanSelected = useMemo(
    () =>
      !!collect(lineItems).firstWhere('product.type', SubscriptionProductType.Plan) ||
      !!collect(lineItems).firstWhere('product.type', SubscriptionProductType.LegacyPlan),
    [lineItems],
  );
  const isGoogleAdsPlanSelected = useMemo(
    () => !!collect(lineItems).firstWhere('product.type', SubscriptionProductType.GoogleAdsPlan),
    [lineItems],
  );

  const isDraft = !data || isEdit;
  const draft = {
    items: discountedLineItems,
    amount,
    tax,
    taxed_amount: total,
  };
  const subscription = data ?? draft;

  const updateProduct = (id, data) =>
    setPersonalProducts((current) =>
      collect(current)
        .map((item) =>
          item.id !== id
            ? item
            : {
                ...item,
                ...data,
              },
        )
        .toArray(),
    );
  const getUnitAmount = (price) => (price.tiers.length > 0 ? price.tiers[0].amount : price.amount);
  const addItem = (item) =>
    setLineItems((current) => {
      const taxCalculator = TaxCalculator.make(item.price.tax_behaviour);

      const lineItem = collect(current).firstWhere('product_id', item.id);

      const price = lineItem?.price ?? item?.price;
      const quantity = (lineItem?.quantity ?? 0) + 1;
      const discount = 0;
      const unitAmount = getUnitAmount(price);
      const amount = (unitAmount - discount) * quantity;

      return collect(current)
        .keyBy('product_id')
        .put(item.id, {
          ...item,
          product_id: item.id,
          quantity,
          product: item,
          price,
          amount,
          tax: taxCalculator.tax(amount, price.tax_percentage),
          taxed_amount: taxCalculator.brutto(amount, price.tax_percentage),
          tax_behaviour: price.tax_behaviour,
          discount: discount * quantity,
        })
        .values()
        .toArray();
    });
  const removeItem = (item) =>
    setLineItems((current) => {
      const taxCalculator = TaxCalculator.make(item.price.tax_behaviour);

      const lineItem = collect(current).firstWhere('product_id', item.id);

      const price = lineItem?.price ?? item?.price;
      const quantity = (lineItem?.quantity ?? 0) - 1;
      const discount = 0;
      const unitAmount = getUnitAmount(price);
      const amount = (unitAmount - discount) * quantity;

      return collect(current)
        .keyBy('product_id')
        .put(item.id, {
          ...item,
          product_id: item.id,
          quantity,
          product: item,
          price,
          amount,
          tax: taxCalculator.tax(amount, price.tax_percentage),
          taxed_amount: taxCalculator.brutto(amount, price.tax_percentage),
          tax_behaviour: price.tax_behaviour,
          discount: discount * quantity,
        })
        .values()
        .where('quantity', '>', 0)
        .toArray();
    });
  const updateItem = (item, data) =>
    setLineItems((current) => {
      const taxCalculator = TaxCalculator.make(data.price.tax_behaviour);

      const lineItem = collect(current).firstWhere('product_id', item.id);
      updateProduct(item.id, {
        ...data,
        price_data: data.price.id ? undefined : data.price,
      });

      if (!lineItem) {
        return current;
      }

      const { price } = data;
      const discount = 0;
      const unitAmount = getUnitAmount(price);
      const amount = (unitAmount - discount) * lineItem.quantity;

      return collect(current)
        .keyBy('product_id')
        .put(item.id, {
          product_id: item.id,
          quantity: lineItem.quantity,
          product: item,
          price,
          amount,
          tax: taxCalculator.tax(amount, price.tax_percentage),
          taxed_amount: taxCalculator.brutto(amount, price.tax_percentage),
          tax_behaviour: data.price.tax_behaviour,
          discount: discount * lineItem.quantity,
          price_id: data.price.id,
          price_data: data.price.id ? undefined : data.price,
        })
        .values()
        .toArray();
    });

  const saveSubscription = async () => {
    const isAnnual = contract.term === SubscriptionTerm.Annual;

    // When a persistence override is supplied (switch-to-annual wizard), hand it
    // the commitment term + discount and let it own the follow-up (staging into
    // `pending_changes`, advancing the wizard) instead of committing here.
    if (onPersist) {
      return onPersist({
        contract_term: contract.term,
        contract_discount_percentage: isAnnual ? clampDiscount(contract.discount, discountMax) : null,
        billing_mode: billingMode,
        trial_days: hasTrial ? trialDays : null,
        items: lineItems,
      });
    }

    // The commitment term + annual discount live on the service contract. Save
    // them first so the backend can apply the discount when it (re)computes the
    // subscription's line items from the original values we send below. The
    // discount is range-validated by AnnualContractDiscount on entry.
    const { error: contractError } = await saveContract({
      apiManager: ServiceContractsManager,
      data: {
        contract_term: contract.term,
        contract_discount_percentage: isAnnual ? clampDiscount(contract.discount, discountMax) : null,
      },
    });

    if (contractError) {
      return toast.error(t('toasts.failed_to_save_contract'));
    }

    const _data = {
      billing_mode: billingMode,
      trial_days: hasTrial ? trialDays : null,
      // Send the original (undiscounted) line items; the backend applies the
      // contract's discount itself.
      items: lineItems,
    };
    const promise = data
      ? updateSubscription({ data: _data, apiManager: SubscriptionsManager, id: data.id })
      : createSubscription({ data: _data, apiManager: SubscriptionsManager });

    const { error } = await promise;

    if (error) {
      return toast.error(t('toasts.failed_to_create_subscription'));
    }

    onSave && onSave();
    await CurrentCompanyManager.get();
    setIsEdit(false);
    moveForward && moveForward();
    ref.current?.scrollTo();
    toast.success(isEdit ? t('toasts.subscription_updated') : t('toasts.subscription_created'));
  };

  if (!hasPaymentOption) {
    return <PaymentInfoStep />;
  }

  if (step === steps.select_products) {
    return (
      <FadeIn>
        {legacyPlans.length > 0 && (
          <div className="mt-md flex justify-center">
            <Segmented
              value={activePlanTab}
              options={Object.values(PlanTab).map((item) => ({
                value: item,
                label: t(`sections.plans.tabs.${item}.name`),
              }))}
              onChange={(value) => setActivePlanTab(value)}
              className="p-[4px]"
            />
          </div>
        )}
        <h3 className="mt-md w-full text-center">{t(`sections.plans.tabs.${activePlanTab}.title`)}</h3>
        {hasTermSelector && (
          <div className="mt-md flex flex-col items-center">
            <Segmented
              value={contract.term}
              options={Object.values(SubscriptionTerm).map((item) => ({
                value: item,
                label: t(`sections.term.options.${item}`),
              }))}
              onChange={(term) => setContract((current) => ({ ...current, term }))}
              className="p-[4px]"
              size="large"
            />
            {contract.term === SubscriptionTerm.Annual && (
              <p className="mt-xs text-center text-sm text-gray-500">{t('sections.term.description')}</p>
            )}
          </div>
        )}
        {contract.term === SubscriptionTerm.Annual && (
          <AnnualContractDiscount
            value={contract.discount}
            onChange={(discount) => setContract((current) => ({ ...current, discount }))}
            editable={can(ManagerPermission.EditAnnualContractDiscount)}
            max={discountMax}
            className="mt-md"
          />
        )}
        <div className="grid gap-8 mt-lg">
          {(activePlanTab === PlanTab.actual ? plans : legacyPlans).map((item) => {
            const lineItem = collect(lineItems).firstWhere('product_id', item.id);

            return (
              <SubscriptionProduct
                data={item}
                lineItem={lineItem}
                prevState={collect(initialPersonalProducts).firstWhere('id', item.id)}
                disabled={isGoogleAdsPlanSelected}
                discount={effectiveDiscount}
                readOnly={isReadOnlyProduct(item)}
                onAdd={() => addItem(item)}
                onUpdate={(data) => updateItem(item, data)}
                onRemove={() => removeItem(item)}
                key={`plan-${item.id}`}
              />
            );
          })}
        </div>
        {otherProducts.length > 0 && (
          <div className="my-md">
            <Tag className="text-uppercase">{t('labels.recommended')}</Tag>
            <h5 className="mt-md">{t('sections.add_ons.title')}</h5>
            <p className="mt-md">{t('sections.add_ons.description')}</p>
            <div className="grid gap-8 mt-md">
              {otherProducts.map((item) => {
                const lineItem = collect(lineItems).firstWhere('product_id', item.id);

                return (
                  <SubscriptionProduct
                    data={item}
                    lineItem={lineItem}
                    disabled={item.type === SubscriptionProductType.GoogleAdsPlan && isPlanSelected}
                    discount={effectiveDiscount}
                    readOnly={isReadOnlyProduct(item)}
                    prevState={collect(initialPersonalProducts).firstWhere('id', item.id)}
                    onAdd={() => addItem(item)}
                    onUpdate={(data) => updateItem(item, data)}
                    onRemove={() => removeItem(item)}
                    variant="secondary"
                    key={`product-${item.id}`}
                  />
                );
              })}
            </div>
          </div>
        )}
        {lineItems?.length > 0 && (
          <Flex className="mt-md" justify="space-between">
            <h6 className="my-auto">
              <Trans t={t} values={{ amount: formatCurrency(amount) }}>
                labels.basket
              </Trans>
            </h6>
            <Flex gap="8px">
              <Button type="primary" onClick={() => setStep(steps.subscribe)}>
                {t('buttons.review_and_subscribe')}
              </Button>
            </Flex>
          </Flex>
        )}
      </FadeIn>
    );
  }

  return (
    <FadeIn ref={ref}>
      <Subscription
        data={isDraft ? draft : subscription}
        isDraft={isDraft}
        billingMode={billingMode}
        setBillingMode={setBillingMode}
        hasTrial={hasTrial}
        setHasTrial={setHasTrial}
        trialDays={trialDays}
        setTrialDays={setTrialDays}
        onEdit={onEdit}
        onSave={onSave}
      />
      {isDraft && (
        <>
          <hr className="my-md" />
          <Agreement
            subscription={draft}
            // Without a trial, go-live starts the backend's default 5-day trial.
            trialDays={hasTrial ? trialDays : 5}
            billDate={nextBillDate({
              data,
              isLive,
              hasTrial,
              trialDays,
            })}
            agreementAccepted={agreementAccepted}
            setAgreementAccepted={setAgreementAccepted}
            agreementText={renderAgreementText?.({
              discount: effectiveDiscount,
              planName: collect(lineItems).first((item) =>
                [SubscriptionProductType.Plan, SubscriptionProductType.LegacyPlan].includes(item.product.type),
              )?.product?.name,
              amount: formatCurrency(total),
            })}
            className="mt-md"
          />
          {lineItems?.length > 0 && (
            <Flex justify="space-between" className="mt-md">
              <h6 className="my-auto">
                <Trans t={t} values={{ amount: formatCurrency(total) }}>
                  labels.basket
                </Trans>
              </h6>
              <Flex justify="end" gap="8px">
                <Button onClick={() => setStep(steps.select_products)}>{t('buttons.basket')}</Button>
                <Button
                  type="primary"
                  onClick={saveSubscription}
                  disabled={!agreementAccepted}
                  loading={isContractSaving || isCreateLoading || isUpdateLoading || isPersisting}
                >
                  {isEdit ? t('buttons.update_subscription') : t('buttons.subscribe')}
                </Button>
              </Flex>
            </Flex>
          )}
        </>
      )}
    </FadeIn>
  );
}
